import { clamp, clone } from 'ramda';
import type { Location, NodeEntry } from 'slate';
import { Editor, Node, Path, Range } from 'slate';

import { arrayEntries } from '../../util';
import type {
  AlineSelection,
  Rect,
  RemoteSelection,
  TableCellElement,
  TableElement,
  TableRowElement,
} from '../internal';
import {
  combineRects,
  isRenderModelTableCell,
  isTableCellElement,
  isTableElement,
  Queries,
} from '../internal';

/** Slack, in px, for grouping selected-cell boxes onto the same top/bottom edge across rounding. */
const GRID_EDGE_EPSILON_PX = 0.5;

/** One slot of the logical grid: what cell occupies a given (row, column) position. */
export interface TableGridSlot {
  isColumnSpanContinuation: boolean;
  isVerticalMergeContinuation: boolean;
  /** The cell physically at this grid row (a 'continue' ghost for merged rows). */
  physicalCellEntry: NodeEntry<TableCellElement>;
  /** The logical (vertical-merge restart) cell occupying this slot. */
  tableCellEntry: NodeEntry<TableCellElement>;
}

/** A rectangular area of a table grid. All indices are inclusive. */
export interface TableGridRect {
  endColumnIndex: number;
  endRowIndex: number;
  startColumnIndex: number;
  startRowIndex: number;
}

/** A logical cell returned by a grid-rect query, with its true extent and merged ghost rows. */
export interface TableCellInGridRect {
  /** The full grid area occupied by this logical cell (may extend beyond the queried rect). */
  occupiedRect: TableGridRect;
  tableCellEntry: NodeEntry<TableCellElement>;
  verticalMergeContinuationEntries: NodeEntry<TableCellElement>[];
}

/** The derived geometry of an active grid selection. */
export interface GridSelectionInfo {
  anchorCellEntry: NodeEntry<TableCellElement>;
  focusCellEntry: NodeEntry<TableCellElement>;
  /** Bounding box of the anchor and focus endpoints (see getGridSelectionEndpointRect). */
  rect: TableGridRect;
  /** The lowest table containing both the anchor and focus cells. */
  tableEntry: NodeEntry<TableElement>;
}

export const tableGridQueries = {
  /** Whether two grid rects overlap. Indices are inclusive on both ends. */
  doTableGridRectsIntersect(rect: TableGridRect, other: TableGridRect): boolean {
    return (
      rect.startColumnIndex <= other.endColumnIndex &&
      rect.endColumnIndex >= other.startColumnIndex &&
      rect.startRowIndex <= other.endRowIndex &&
      rect.endRowIndex >= other.startRowIndex
    );
  },

  /** Expand each collaborator's grid selection into one highlight range per selected cell. */
  expandRemoteGridSelections(
    editor: Editor,
    remoteSelections: Record<string, RemoteSelection> | null
  ): Record<string, RemoteSelection> | null {
    if (!remoteSelections) return remoteSelections;

    const expanded: Record<string, RemoteSelection> = {};
    for (const [clientId, remoteSelection] of Object.entries(remoteSelections)) {
      const info = Queries.getGridSelectionInfo(editor, { selection: remoteSelection });

      expanded[clientId] = { ...remoteSelection, isGridSelection: info !== null };
      if (!info) continue;

      const cellEntries = Queries.getTableCellEntriesInGridRect(info.tableEntry, info.rect).flatMap(
        ({ tableCellEntry, verticalMergeContinuationEntries }) => [
          tableCellEntry,
          ...verticalMergeContinuationEntries,
        ]
      );
      for (const [, cellPath] of cellEntries) {
        expanded[`${clientId}#${cellPath.toString()}`] = {
          ...remoteSelection,
          ...Editor.range(editor, cellPath),
          gridCellHighlight: true,
        };
      }
    }

    return expanded;
  },

  /**
   * The selected logical cells of the active grid selection in path order, or null when none is
   * active. With includeVerticalMergeContinuations, each merged cell's ghost 'continue' rows follow
   * its restart cell
   */
  getGridSelectionCellEntries(
    editor: Editor,
    options: { includeVerticalMergeContinuations?: boolean } = {}
  ): NodeEntry<TableCellElement>[] | null {
    const { includeVerticalMergeContinuations = false } = options;

    const info = Queries.getGridSelectionInfo(editor);
    if (!info) return null;

    const cellsInRect = Queries.getTableCellEntriesInGridRect(info.tableEntry, info.rect);

    return cellsInRect.flatMap(({ tableCellEntry, verticalMergeContinuationEntries }) =>
      includeVerticalMergeContinuations
        ? [tableCellEntry, ...verticalMergeContinuationEntries]
        : [tableCellEntry]
    );
  },

  /**
   * A standalone table of exactly the grid selection's rectangle, suitable as a copy fragment, or
   * null when no grid selection is active. Merged cells clipped by the rect have their columnSpan
   * clamped to the overlap so every emitted row has aligned physical cells; a vertically merged cell
   * whose restart lies above the rect becomes a fresh 'restart' on the rect's first row. (Cloned
   * locationIds are regenerated by cleanContentNodes on paste, so they need not be stripped here.)
   */
  getGridSelectionFragment(editor: Editor): TableElement | null {
    const info = Queries.getGridSelectionInfo(editor);
    if (!info) return null;

    const { rect, tableEntry } = info;
    const [table] = tableEntry;
    const matrix = Queries.getTableGridMatrix(tableEntry);

    const rows: TableRowElement[] = [];
    for (let rowIndex = rect.startRowIndex; rowIndex <= rect.endRowIndex; rowIndex++) {
      const sourceRow = table.children.at(rowIndex);
      const matrixRow = matrix.at(rowIndex);
      if (!sourceRow || !matrixRow) continue;

      const cells: TableCellElement[] = [];
      const seenPhysicalCells = new Set<string>();
      for (
        let columnIndex = rect.startColumnIndex;
        columnIndex <= rect.endColumnIndex;
        columnIndex++
      ) {
        const slot = matrixRow.at(columnIndex);
        if (!slot) continue;

        const [physicalCell, physicalCellPath] = slot.physicalCellEntry;
        const physicalCellKey = physicalCellPath.join(',');
        if (seenPhysicalCells.has(physicalCellKey)) continue;
        seenPhysicalCells.add(physicalCellKey);

        const [, logicalCellPath] = slot.tableCellEntry;
        const logicalCellRowIndex = logicalCellPath.at(-2) ?? 0;
        const restartsOutsideRect =
          slot.isVerticalMergeContinuation && logicalCellRowIndex < rect.startRowIndex;

        /**
         * Clamp the cell's column span to the part that overlaps the rect, so a merged cell clipped
         * by the rect emits an aligned row. Without this a copied row containing a merged cell has
         * fewer physical cells than a full row, and paste-time row padding inserts a spurious cell.
         */
        const occupiedRect = Queries.getTableCellOccupiedRect(editor, slot.physicalCellEntry);
        const clampedColumnSpan =
          Math.min(occupiedRect.endColumnIndex, rect.endColumnIndex) -
          Math.max(occupiedRect.startColumnIndex, rect.startColumnIndex) +
          1;
        const columnSpan = clampedColumnSpan > 1 ? clampedColumnSpan : undefined;

        cells.push(
          restartsOutsideRect && rowIndex === rect.startRowIndex
            ? { ...physicalCell, columnSpan, verticalMerge: 'restart' }
            : { ...physicalCell, columnSpan }
        );
      }

      if (cells.length > 0) rows.push({ ...sourceRow, children: cells });
    }

    if (rows.length === 0) return null;

    return clone({ ...table, children: rows });
  },

  /**
   * Derive the geometry of a grid selection from its two endpoints, or null when the selection is not
   * a grid selection: collapsed, an endpoint no longer resolves, both endpoints in one logical cell,
   * or not within a common table. Grid-ness is purely geometric — a selection is a grid selection iff
   * its anchor and focus land in two distinct cells of one table. Consumers treat
   * null as "fall back to linear behavior". The rect is the bounding box of the two endpoint rects,
   * each anchored to its recorded pointer column (see getGridSelectionEndpointRect).
   */
  getGridSelectionInfo(
    editor: Editor,
    options: { selection?: AlineSelection | null } = {}
  ): GridSelectionInfo | null {
    const { selection = editor.selection } = options;
    if (!selection || Range.isCollapsed(selection)) return null;
    if (!Queries.hasRange(editor, selection)) return null;

    const { anchor, focus } = selection;
    const [anchorCellEntry, focusCellEntry] = [anchor, focus].map(point =>
      Editor.above(editor, { at: point, match: isTableCellElement, mode: 'lowest' })
    );
    if (!anchorCellEntry || !focusCellEntry) return null;

    const commonPath = Path.common(anchorCellEntry[1], focusCellEntry[1]);
    // Both endpoints inside one cell (possibly a nested table) is a plain text selection.
    if (isTableCellElement(Node.get(editor, commonPath))) return null;

    /**
     * The highest cell above each endpoint under the common ancestor: the endpoints' cells in the
     * lowest table containing both (nested tables collapse into their containing outer cell).
     */
    const [highestAnchorCellEntry, highestFocusCellEntry] = [anchor, focus].map(point =>
      Queries.matchAbove<TableCellElement>(editor, {
        at: point,
        match: (node, path) => isTableCellElement(node) && Path.isDescendant(path, commonPath),
        mode: 'highest',
      })
    );
    if (!highestAnchorCellEntry || !highestFocusCellEntry) return null;

    const tableEntry = Queries.atOrAbove(editor, {
      at: commonPath,
      match: isTableElement,
      mode: 'lowest',
    });
    if (!tableEntry) return null;

    const [, tablePath] = tableEntry;
    const cellsBelongToTable = [highestAnchorCellEntry, highestFocusCellEntry].every(
      ([, cellPath]) =>
        Path.isAncestor(tablePath, cellPath) && cellPath.length === tablePath.length + 2
    );
    if (!cellsBelongToTable) return null;

    const anchorLogicalCellEntry = Queries.getVerticallyMergedTableCellStart(
      editor,
      highestAnchorCellEntry
    );
    const focusLogicalCellEntry = Queries.getVerticallyMergedTableCellStart(
      editor,
      highestFocusCellEntry
    );
    // Both endpoints in one logical cell (e.g. a restart cell and its ghost) is not a grid.
    if (Path.equals(anchorLogicalCellEntry[1], focusLogicalCellEntry[1])) return null;

    const anchorRect = getGridSelectionEndpointRect(
      Queries.getTableCellOccupiedRect(editor, anchorLogicalCellEntry),
      selection.gridSelectionAnchorColumn
    );
    const focusRect = getGridSelectionEndpointRect(
      Queries.getTableCellOccupiedRect(editor, focusLogicalCellEntry),
      selection.gridSelectionFocusColumn
    );
    const rect: TableGridRect = {
      endColumnIndex: Math.max(anchorRect.endColumnIndex, focusRect.endColumnIndex),
      endRowIndex: Math.max(anchorRect.endRowIndex, focusRect.endRowIndex),
      startColumnIndex: Math.min(anchorRect.startColumnIndex, focusRect.startColumnIndex),
      startRowIndex: Math.min(anchorRect.startRowIndex, focusRect.startRowIndex),
    };

    return {
      anchorCellEntry: anchorLogicalCellEntry,
      focusCellEntry: focusLogicalCellEntry,
      rect,
      tableEntry,
    };
  },

  /**
   * Like getGridSelectionInfo, but for callers whose `at` defaults to the selection: returns the info
   * only when `at` IS the live selection — by identity, or by value equality (a captured selection
   * loses object identity once its props are mutated). Unrelated explicit locations return null.
   */
  getGridSelectionInfoForLocation(
    editor: Editor,
    options: { at: Location | null }
  ): GridSelectionInfo | null {
    const { at } = options;
    if (!at) return null;

    const isLiveSelection =
      at === editor.selection ||
      (!!editor.selection && Range.isRange(at) && Range.equals(at, editor.selection));

    return isLiveSelection ? Queries.getGridSelectionInfo(editor) : null;
  },

  /**
   * Document-space rects anchoring the top and bottom edges of the active grid selection, or null
   * when none is active. Unlike a linear range — whose rects trace the anchor→focus text run — these
   * bound the highlighted cell rectangle, so callers like the selection popover can anchor to what is
   * actually selected. Coordinates match getRangeLineIntersectionRectInDocument: topPx from the top
   * of the scroll window, leftPx from the left of the page. A selection inside a header or footer is
   * measured on the open page only, since every page renders its own copy of that header or footer.
   */
  getGridSelectionRectsInDocument(
    editor: Editor,
    options: { activeHeaderOrFooterPageNum: number | null }
  ): { firstLineRect: Rect; lastLineRect: Rect } | null {
    const { activeHeaderOrFooterPageNum } = options;

    const cellEntries = Queries.getGridSelectionCellEntries(editor, {
      includeVerticalMergeContinuations: true,
    });
    if (!cellEntries) return null;

    const cellRects = cellEntries.flatMap(([, cellPath]) =>
      getTableCellRectsInDocument(editor, cellPath, { activeHeaderOrFooterPageNum })
    );
    if (cellRects.length === 0) return null;

    const top = Math.min(...cellRects.map(rect => rect.topPx));
    const bottom = Math.max(...cellRects.map(rect => rect.topPx + rect.heightPx));

    const topRow = cellRects.filter(rect => rect.topPx <= top + GRID_EDGE_EPSILON_PX);
    const bottomRow = cellRects.filter(
      rect => rect.topPx + rect.heightPx >= bottom - GRID_EDGE_EPSILON_PX
    );

    return {
      firstLineRect: topRow.reduce(combineRects),
      lastLineRect: bottomRow.reduce(combineRects),
    };
  },

  /** Whether the current (or given) selection is a grid selection */
  getIsGridSelection(editor: Editor, options: { selection?: AlineSelection | null } = {}): boolean {
    return Queries.getGridSelectionInfo(editor, options) !== null;
  },

  /**
   * Re-validate a selection's grid pointer coordinates: they are kept only while the selection still
   * derives a valid grid selection, otherwise both are nulled.
   */
  getNormalizedGridSelectionProps(
    editor: Editor,
    selection: AlineSelection
  ): Pick<AlineSelection, 'gridSelectionAnchorColumn' | 'gridSelectionFocusColumn'> {
    const isValidGridSelection = !!Queries.getGridSelectionInfo(editor, { selection });

    return {
      gridSelectionAnchorColumn: isValidGridSelection
        ? (selection.gridSelectionAnchorColumn ?? null)
        : null,
      gridSelectionFocusColumn: isValidGridSelection
        ? (selection.gridSelectionFocusColumn ?? null)
        : null,
    };
  },

  /**
   * The logical cells whose occupied area intersects the given rect, in path order. A merged cell is
   * included whole whenever any part of it overlaps — matching Word's "ragged" selections rather than
   * expanding the rect — so a returned cell's occupiedRect may extend beyond it. Each cell carries
   * its 'continue' ghost rows for painting/props.
   */
  getTableCellEntriesInGridRect(
    tableEntry: NodeEntry<TableElement>,
    rect: TableGridRect
  ): TableCellInGridRect[] {
    const matrix = Queries.getTableGridMatrix(tableEntry);

    const cellsByLogicalPath = new Map<string, TableCellInGridRect>();
    for (const [rowIndex, matrixRow] of arrayEntries(matrix)) {
      for (const [columnIndex, slot] of arrayEntries(matrixRow)) {
        if (!slot) continue;

        const logicalPathKey = slot.tableCellEntry[1].join(',');
        const existing = cellsByLogicalPath.get(logicalPathKey);
        if (!existing) {
          cellsByLogicalPath.set(logicalPathKey, {
            occupiedRect: {
              endColumnIndex: columnIndex,
              endRowIndex: rowIndex,
              startColumnIndex: columnIndex,
              startRowIndex: rowIndex,
            },
            tableCellEntry: slot.tableCellEntry,
            verticalMergeContinuationEntries: [],
          });
          continue;
        }

        existing.occupiedRect.endColumnIndex = Math.max(
          existing.occupiedRect.endColumnIndex,
          columnIndex
        );
        existing.occupiedRect.endRowIndex = Math.max(existing.occupiedRect.endRowIndex, rowIndex);
      }
    }

    /**
     * Collect each logical cell's ghost 'continue' rows once, from the first column slot of each
     * ghost row (a columnSpan repeats the same physical ghost across its columns).
     */
    for (const matrixRow of matrix) {
      for (const slot of matrixRow) {
        if (!slot?.isVerticalMergeContinuation || slot.isColumnSpanContinuation) continue;

        const logicalPathKey = slot.tableCellEntry[1].join(',');
        cellsByLogicalPath
          .get(logicalPathKey)
          ?.verticalMergeContinuationEntries.push(slot.physicalCellEntry);
      }
    }

    return [...cellsByLogicalPath.values()]
      .filter(({ occupiedRect }) => Queries.doTableGridRectsIntersect(occupiedRect, rect))
      .toSorted(({ tableCellEntry: [, pathA] }, { tableCellEntry: [, pathB] }) =>
        Path.compare(pathA, pathB)
      );
  },

  /**
   * The full grid area a logical cell occupies: its columnSpan columns × its vertical-merge group's
   * rows. A 'continue' ghost resolves to its restart cell first, so any cell of a merged group
   * returns the whole group's rect.
   */
  getTableCellOccupiedRect(
    editor: Editor,
    tableCellEntry: NodeEntry<TableCellElement>
  ): TableGridRect {
    const startEntry = Queries.getVerticallyMergedTableCellStart(editor, tableCellEntry);
    const [startCell, startCellPath] = startEntry;

    const startColumnIndex = Queries.getTableCellStartColumn(editor, startEntry);
    const endColumnIndex = Queries.getTableCellEndColumnFromStartColumn(
      startCell,
      startColumnIndex
    );

    const startRowIndex = startCellPath.at(-2) ?? 0;
    const group = Queries.getVerticallyMergedTableCellGroup(editor, startEntry);
    const endRowIndex = startRowIndex + group.length - 1;

    return { endColumnIndex, endRowIndex, startColumnIndex, startRowIndex };
  },

  /**
   * Build the logical grid of a table: matrix[rowIndex][columnIndex] is the slot occupying that grid
   * position, or null for a gap in a row shorter than the table's column count. Horizontally merged
   * cells fill every column of their span; vertically merged 'continue' ghosts resolve up to their
   * restart cell as the slot's logical cell.
   */
  getTableGridMatrix(tableEntry: NodeEntry<TableElement>): (TableGridSlot | null)[][] {
    const [table, tablePath] = tableEntry;
    const columnCount = Queries.getTableColumnCount(table);

    const matrix: (TableGridSlot | null)[][] = [];
    for (const [rowIndex, tableRow] of arrayEntries(table.children)) {
      const matrixRow: (TableGridSlot | null)[] = new Array(columnCount).fill(null);
      matrix.push(matrixRow);

      let startColumnIndex = 0;
      for (const [cellIndex, tableCell] of arrayEntries(tableRow.children)) {
        const endColumnIndex = Queries.getTableCellEndColumnFromStartColumn(
          tableCell,
          startColumnIndex
        );
        const physicalCellEntry: NodeEntry<TableCellElement> = [
          tableCell,
          [...tablePath, rowIndex, cellIndex],
        ];

        /**
         * A 'continue' cell extends the logical cell above it in the same column. A 'continue' with
         * no merged cell above (the cell above has no verticalMerge) instead starts a fresh logical
         * cell, mirroring getVerticallyMergedTableCellStart.
         */
        const previousRowSlot =
          rowIndex > 0 ? (matrix[rowIndex - 1]?.[startColumnIndex] ?? null) : null;
        const isVerticalMergeContinuation =
          Queries.renderedTableCellVerticalMerge(tableCell) === 'continue' &&
          !!previousRowSlot &&
          Queries.renderedTableCellVerticalMerge(previousRowSlot.tableCellEntry[0]) !== undefined;
        const tableCellEntry = isVerticalMergeContinuation
          ? previousRowSlot.tableCellEntry
          : physicalCellEntry;

        for (
          let columnIndex = startColumnIndex;
          columnIndex <= Math.min(endColumnIndex, columnCount - 1);
          columnIndex++
        ) {
          matrixRow[columnIndex] = {
            isColumnSpanContinuation: columnIndex > startColumnIndex,
            isVerticalMergeContinuation,
            physicalCellEntry,
            tableCellEntry,
          };
        }

        startColumnIndex = endColumnIndex + 1;
      }
    }

    return matrix;
  },
};

/**
 * Reduce an endpoint's occupied rect to the slot that anchors the selection. A vertically merged
 * endpoint anchors to its top row (still selected whole via rect intersection) so extending grows
 * down a row at a time, matching Word. A recorded pointer column (mouse) also narrows a horizontally
 * merged endpoint to the clicked column.
 */
function getGridSelectionEndpointRect(
  occupiedRect: TableGridRect,
  column: number | null | undefined
): TableGridRect {
  const { endColumnIndex, startColumnIndex, startRowIndex } = occupiedRect;
  const clickedColumnIndex =
    typeof column === 'number' ? clamp(startColumnIndex, endColumnIndex, column) : null;

  return {
    endColumnIndex: clickedColumnIndex ?? endColumnIndex,
    endRowIndex: startRowIndex,
    startColumnIndex: clickedColumnIndex ?? startColumnIndex,
    startRowIndex,
  };
}

/**
 * The document-space boxes of a table cell — one per render-model fragment, so a cell split across a
 * page boundary yields a box on each page, and a cell in a header or footer only a box on the open
 * page. Empty when the cell has no render-model representation.
 */
function getTableCellRectsInDocument(
  editor: Editor,
  cMCellPath: Path,
  options: { activeHeaderOrFooterPageNum: number | null }
): Rect[] {
  const { activeHeaderOrFooterPageNum } = options;

  const rMPaths = Queries.getRMLocationsForActiveHeaderOrFooter(
    editor,
    Queries.getRMPathsFromCMPath(editor, cMCellPath),
    activeHeaderOrFooterPageNum
  );

  const rects: Rect[] = [];
  for (const rMPath of rMPaths) {
    const pageNum = Queries.getPageNumberForRMPath(editor, rMPath);
    if (pageNum === null) continue;

    const [rMNode, rMNodePath] = Queries.renderModelNode(editor, rMPath);
    if (!isRenderModelTableCell(rMNode)) continue;

    const { xLeft, xRight, yBottom, yTop } = Queries.getRMNodeAbsolutePosition(editor, [
      rMNode,
      rMNodePath,
    ]);
    rects.push({
      heightPx: yBottom - yTop,
      leftPx: xLeft,
      topPx: yTop + Queries.getTopOfPagePx(editor, pageNum),
      widthPx: xRight - xLeft,
    });
  }
  return rects;
}
